// Phase 10 — AuthProvider migrated to refresh-token flow.
//
// The /api/auth/login endpoint now accepts ?withRefresh=1 and returns
// both the legacy long-lived `token` AND a short-lived `accessToken` +
// a `refreshToken` (also sent as the ss_refresh cookie).
//
// We always pass ?withRefresh=1. The provider:
//   - stores the access token in localStorage (`dk_token`) — that's the
//     bearer that the api client sends on every request
//   - lets the api client handle silent refresh transparently on 401
//   - exposes signOut that calls the server's /api/auth/logout so the
//     refresh-token row is revoked server-side
//
// Old clients with just `token` still work — the new code reads either
// `accessToken` (preferred) or `token` (legacy) so the migration is safe.

import { createContext, useContext, useEffect, useState, ReactNode, startTransition } from 'react';
import { authApi, getToken, setToken, clearToken, authSessionsApi } from '@/integrations/api/client';

interface UserType {
  id: string;
  email: string;
  displayName: string;
  role: string;
  orgId: string;
}

interface AuthContextType {
  user: UserType | null;
  session: any;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, displayName?: string, companyName?: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Phase 31 BUG 2 — logout-on-refresh fix.
//
// Login returns BOTH a long-lived `token` (JWT_EXPIRES_IN, default 7d) and a
// short-lived `accessToken` (ACCESS_TOKEN_TTL_MIN, default 15m). We used to
// store the 15-minute accessToken as the bearer, so once it expired a page
// refresh depended entirely on the ss_refresh cookie round-trip — and if that
// failed (cookie/proxy/SameSite), /me 401'd and the user was logged out.
//
// Prefer the long-lived `token` as the persisted bearer: it survives refresh
// from localStorage for its full TTL, so reloading the page keeps the user
// signed in without a network round-trip. The api client's silent-refresh on
// 401 still runs as a fallback. (Tradeoff: longer-lived bearer vs short-token
// rotation — acceptable per the "must not log out on refresh" requirement.)
function extractToken(data: any): string | undefined {
  return data?.token || data?.accessToken;
}

// Phase 31 BUG 1 — cache the user alongside the token so a page refresh can
// render the authenticated UI immediately (optimistic hydrate) instead of
// hanging on /me and bouncing to /auth on any hiccup.
const USER_KEY = 'dk_user';
function cacheUser(u: UserType | null) {
  try { u ? localStorage.setItem(USER_KEY, JSON.stringify(u)) : localStorage.removeItem(USER_KEY); } catch { /* ignore */ }
}
function readCachedUser(): UserType | null {
  try { const raw = localStorage.getItem(USER_KEY); return raw ? JSON.parse(raw) as UserType : null; } catch { return null; }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  // Hydrate optimistically from the cached user so a refresh with a valid
  // token never flickers to the login screen.
  const [user, setUser] = useState<UserType | null>(() => (getToken() ? readCachedUser() : null));
  const [loading, setLoading] = useState(() => !!getToken() && !readCachedUser());

  useEffect(() => {
    const token = getToken();
    // Phase 31 BUG 1 — temporary diagnostics (remove once verified).
    console.debug('[auth] mount — token present?', !!token, '· cached user?', !!readCachedUser());
    if (!token) { setLoading(false); return; }
    authApi.me()
      .then(data => {
        console.debug('[auth] /me ok →', data?.user?.email);
        setUser(data.user);
        cacheUser(data.user);
      })
      .catch((err: any) => {
        // Phase 16.1 / 20.1: only clear on a DEFINITIVE auth rejection
        // (401/403). For 5xx / network / transient errors we keep the
        // cached session in place — the next request retries. Phase 31:
        // because we now hydrate optimistically, a transient /me failure
        // no longer logs the user out; only a real 401/403 does.
        const status = Number(err?.status);
        const isAuthFail = status === 401 || status === 403;
        console.debug('[auth] /me failed — status:', status, '· willLogout:', isAuthFail, '·', err?.message);
        if (isAuthFail) { clearToken(); setUser(null); }
      })
      // Phase 20.1 — wrap the loading-state flip in startTransition so a
      // token rejection that triggers ProtectedRoute → <Navigate to="/auth">
      // doesn't trip React 426 on any lazy boundary along the way.
      .finally(() => startTransition(() => setLoading(false)));
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      // Phase 10: opt into the refresh-token flow. Cookie is set
      // server-side; the api client picks it up automatically on /refresh.
      const data = await authApi.login(email, password, { withRefresh: true });
      const tok = extractToken(data);
      if (tok) setToken(tok);
      setUser(data.user);
      cacheUser(data.user);
      return { error: null };
    } catch (err: any) {
      return { error: new Error(err.message || 'Login failed') };
    }
  };

  const signUp = async (email: string, password: string, displayName?: string, companyName?: string) => {
    try {
      const data = await authApi.register(email, password, displayName, companyName);
      const tok = extractToken(data);
      if (tok) setToken(tok);
      setUser(data.user);
      cacheUser(data.user);
      return { error: null };
    } catch (err: any) {
      return { error: new Error(err.message || 'Registration failed') };
    }
  };

  const signOut = async () => {
    // Best-effort server-side revoke. If it fails the local cleanup still happens.
    try { await authSessionsApi.logout(); } catch {}
    clearToken();
    cacheUser(null);
    setUser(null);
    // Unified ecosystem logout: clear the central SSO session + shared cookie,
    // then land on the sign-in page.
    window.location.href = '/api/sso/logout?redirect=' + encodeURIComponent(window.location.origin + '/auth');
  };

  return (
    <AuthContext.Provider value={{ user, session: user, loading, signIn, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
}
